/**
 * ThemeProvider — tema dia/noite do redesign.
 *
 * - LIGHT é o padrão (o app abre claro). DARK é opt-in via toggle.
 * - Persiste em localStorage['es-theme'].
 * - Seta `data-theme` no <body> (os tokens em styles/tokens.css trocam por isso).
 * - Atualiza <meta name="theme-color"> (#f5f7fa claro / #0a111e escuro).
 *
 * Para evitar flash, chame `applyStoredTheme()` uma vez, síncrono, antes do
 * primeiro render (feito no main.tsx).
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "es-theme";
const META_LIGHT = "#f5f7fa";
const META_DARK = "#0a111e";

export function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "dark" || v === "light") return v;
  } catch {
    /* localStorage indisponível — cai no default */
  }
  return "light";
}

function applyTheme(theme: Theme) {
  const body = document.body;
  if (theme === "dark") body.setAttribute("data-theme", "dark");
  else body.removeAttribute("data-theme");
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "dark" ? META_DARK : META_LIGHT);
}

/** Aplica o tema salvo antes do render (chamado no main.tsx). */
export function applyStoredTheme() {
  applyTheme(readStoredTheme());
}

interface ThemeCtx {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return <Ctx.Provider value={{ theme, toggleTheme, setTheme }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme deve ser usado dentro de <ThemeProvider>");
  return ctx;
}
